
import { useEffect, useMemo, useRef, useState } from "react"
import JoditEditor, { Jodit } from 'jodit-react';
import { Box } from "@mui/material";
import { openBackgroundImageUploader } from "./utils/DescriptionEditorBlockUtils";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { fetchStepBgImgFileContent } from "./utils/commonUtils";

export const DescriptionEditorContainer = (props) => {
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <Box 
            width="100%" 
            sx={{
                flex: { xs: "0 0 auto", md: "1 1 auto" },
                minHeight: { xs: "400px", md: "300px" },
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    borderBottom: 1, 
                    borderColor: '#e9ecef',
                    backgroundColor: '#fafafa'
                }}
            >
                <Tabs 
                    value={tabIndex} 
                    onChange={handleTabChange} 
                    aria-label="language tabs"
                    sx={{
                        minHeight: '40px',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                            minHeight: '40px',
                            px: 2,
                            color: '#6c757d',
                            '&.Mui-selected': {
                                color: '#dc3545',
                                fontWeight: 600
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#dc3545',
                            height: 2
                        }
                    }}
                >
                    <Tab label="English" {...a11yProps(0)} />
                    <Tab label="Norwegian" {...a11yProps(1)} />
                </Tabs>
            </Box>
            <CustomTabPanel value={tabIndex} index={0}>
                <DescriptionEditorBlock {...props} tabIndex={tabIndex} />
            </CustomTabPanel>
            <CustomTabPanel value={tabIndex} index={1}>
                <DescriptionEditorBlock {...props} tabIndex={tabIndex} />
            </CustomTabPanel>
        </Box>
    );
};

export const DescriptionEditorBlock = ({ selectedStep, stepContentState, setStepContentState, tabIndex }) => {
    const editorInstance = useRef();

    const config = useMemo(
        () => {
            const obj = {
                buttons: [{
                    name: 'Background Image',
                    tooltip: 'Insert Background Image',
                    exec: (editor) => {
                        openBackgroundImageUploader(editor, setStepContentState)
                    }
                },
                ...Jodit.defaultOptions.buttons,    
                ],
                pasteHTML: true,
                askBeforePasteFromWord: false,
                askBeforePasteHTML: false,
                maxLength: 10000000,
                defaultActionOnPaste: 'insert_as_html',
                events: {
                    afterInit(instance) {
                        editorInstance.current = instance;
                    },
                },
            }
            return obj
        },
        [setStepContentState]
    );

    useEffect(() => {

        if (!editorInstance?.current || !selectedStep) return;

        (async () => {
            try {
                const res = await fetchStepBgImgFileContent(selectedStep.stepBgImgFileName, selectedStep)
                const blob = new Blob([res.data], { type: "image/jpeg" });
                const bgImgBlobUrl = URL.createObjectURL(blob);

                // Set the selected image as the editor's background
                editorInstance.current.editor.style.backgroundImage = `url(${bgImgBlobUrl})`;
                editorInstance.current.editor.style.backgroundSize = "cover"; // Adjust to fit the container
                editorInstance.current.editor.style.backgroundRepeat = "no-repeat";
            } catch (error) {
                // Nothing to do
            }

        })()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorInstance?.current, selectedStep])

    const editorVal = useMemo(() => {
        if (tabIndex === 0) {
            return stepContentState.englishDescriptionContent
        } else if (tabIndex === 1) {
            return stepContentState.norwegianDescriptionContent
        } else {
            return ""
        }
    }, [tabIndex, stepContentState.englishDescriptionContent, stepContentState.norwegianDescriptionContent])

    const handleBlurEditor = (newContent) => {
        if (tabIndex === 0) {
            setStepContentState(prevState => ({ ...prevState, englishDescriptionContent: newContent }));
        } else if (tabIndex === 1) {
            setStepContentState(prevState => ({ ...prevState, norwegianDescriptionContent: newContent }));
        } else {
            return ""
        }
    }

    return (
        <Box 
            width="100%" 
            sx={{ 
                flex: 1,
                display: 'flex', 
                flexDirection: 'column', 
                pr: 1, 
                pb: 1,
                overflow: 'auto',
                minHeight: 0
            }}
        >
            <JoditEditor
                value={editorVal}
                onBlur={handleBlurEditor}
                config={config}
            />
        </Box>
    );
};


function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}